import { Clock } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// C24: shared-device (kiosk) safety. On a classroom device that many students
// use in turn, a session left open lets the next child act as the previous one.
// After a period of inactivity we warn the user and, if there's no response,
// sign them out — returning to the login screen so the next student can sign in
// (a "fast user switch"). The same modal offers an explicit "Switch student"
// button for deliberate hand-offs.

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

interface IdleTimeoutWarningProps {
  /** inactivity before the warning appears (default 15 min) */
  idleMs?: number;
  /** how long the warning counts down before auto sign-out (default 60s) */
  warningMs?: number;
  /** fired when the countdown elapses with no response */
  onTimeout: () => void;
  /** fired when the user explicitly chooses to switch/sign out now */
  onSwitchUser: () => void;
}

export function IdleTimeoutWarning({
  idleMs = 15 * 60_000,
  warningMs = 60_000,
  onTimeout,
  onSwitchUser,
}: IdleTimeoutWarningProps) {
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(Math.ceil(warningMs / 1000));
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stayButtonRef = useRef<HTMLButtonElement>(null);

  const startIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setWarning(true), idleMs);
  }, [idleMs]);

  // Reset the idle timer on any activity — but only while not already warning
  // (once warning, the countdown must be answered explicitly).
  useEffect(() => {
    if (warning) return;
    startIdleTimer();
    const onActivity = () => startIdleTimer();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [warning, startIdleTimer]);

  // Countdown while the warning is showing.
  useEffect(() => {
    if (!warning) return;
    setRemaining(Math.ceil(warningMs / 1000));
    stayButtonRef.current?.focus();
    const interval = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [warning, warningMs]);

  // Fire the sign-out when the countdown reaches zero.
  useEffect(() => {
    if (warning && remaining === 0) onTimeout();
  }, [warning, remaining, onTimeout]);

  const stay = useCallback(() => setWarning(false), []);

  useEffect(() => {
    if (!warning) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [warning, stay]);

  if (!warning) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      aria-hidden="true"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        aria-describedby="idle-desc"
        className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <Clock className="h-7 w-7" aria-hidden="true" />
        </div>
        <h2 id="idle-title" className="mt-4 text-lg font-semibold text-gray-900">
          Are you still there?
        </h2>
        <p id="idle-desc" className="mt-2 text-sm text-gray-600">
          To keep this device safe, we’ll sign you out in{' '}
          <span className="font-semibold tabular-nums">{remaining}</span> second
          {remaining === 1 ? '' : 's'}.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <button
            ref={stayButtonRef}
            onClick={stay}
            className="w-full rounded-xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            I’m still here
          </button>
          <button
            onClick={onSwitchUser}
            className="w-full rounded-xl px-6 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            Switch student / Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
